import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Button, Stack, Typography } from '@mui/material';
import { getSelectorAttribute, headerSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { JOB_TASK_STATUSES } from '@vy/jobs-types';
import { isNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import { useDismissJobNotification } from '../../../../hooks/requests/use-dismiss-job-notification';
import { useDialog } from '../../../../hooks/use-dialog';
import { VyTasksModal } from '../VyTasksModal/VyTasksModal';

export type NotificationStatus = 'syncing' | 'done' | 'error';

export type NotificationCardProps = {
  id: string;
  title: string;
  metadata: string;
  triggeredAt: string;
  status: NotificationStatus;
  readAt?: Date | null;
  onViewReport?: () => void;
};

export const VyNotificationCard: React.FC<NotificationCardProps> = ({
  id,
  title,
  metadata,
  triggeredAt,
  status,
  readAt,
  onViewReport,
}) => {
  const { isDialogOpen, handleClose, handleOpen } = useDialog(false);
  const isUnread = isNullOrUndefined(readAt);

  const { mutate: dismissNotification, isLoading: isDismissing } = useDismissJobNotification();
  const taskStatusFilter = status === 'error' ? JOB_TASK_STATUSES.Failed : undefined;
  const isFailed = status === 'error';

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismissNotification(id);
  };

  const notificationStatusIcon = useMemo(() => {
    switch (status) {
      case 'syncing':
        return (
          <Box
            sx={{
              '@keyframes spin': {
                from: {
                  transform: 'scale(0.8) rotate(0deg)',
                },
                to: {
                  transform: 'scale(0.8) rotate(360deg)',
                },
              },
              animation: 'spin 1s linear infinite',
            }}
          >
            <VyIcon
              icon="ArrowRotate"
              color="#ed8822"
              {...getSelectorAttribute(
                headerSelectors.notifications.notificationCardContent.cardIcon.loading,
              )}
            />
          </Box>
        );
      case 'done':
        return (
          <VyIcon
            icon="CheckCircle"
            color="#42ae5c"
            style={{ transform: 'scale(0.8)' }}
            {...getSelectorAttribute(headerSelectors.notifications.notificationCardContent.cardIcon.success)}
          />
        );
      case 'error':
        return (
          <VyIcon
            icon="Warning"
            color="#ee2049"
            style={{ transform: 'scale(0.8)' }}
            {...getSelectorAttribute(headerSelectors.notifications.notificationCardContent.cardIcon.failed)}
          />
        );
    }
  }, [status]);

  const notificationIcon = useMemo(() => {
    switch (status) {
      case 'syncing':
        return <VyIcon icon="TwoDirectionalArrow" color="#2953ff" />;
      case 'done':
        return <VyIcon icon="TwoDirectionalArrow" color="#2953ff" />;
      case 'error':
        return <VyIcon icon="Warning" color="#ee2049" style={{ transform: 'scale(0.8)' }} />;
    }
  }, [status]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        gap: 1,
        borderRadius: '12px',
        padding: '16px',
        transition: 'background-color 0.5s ease, border-color 0.5s ease',
        cursor: 'pointer',
        ...(isUnread
          ? {
              bgcolor: '#f5f9fe',
              border: '1px solid #B5D8FF',
            }
          : { bgcolor: 'white' }),
        '&:hover': {
          ...(!isUnread
            ? {
                bgcolor: '#f5f6fa',
              }
            : {}),
        },
      }}
    >
      <Box>{notificationIcon}</Box>
      <Box sx={{ flex: 1 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 1
          }}>
          <Typography
            {...getSelectorAttribute(headerSelectors.notifications.notificationCardContent.cardTitle)}
            sx={{
              fontWeight: 580,
              fontSize: 14,
              color: '#0D1B27'
            }}>
            {title}
          </Typography>
          <Typography
            {...getSelectorAttribute(headerSelectors.notifications.notificationCardContent.cardTitle)}
            sx={{
              fontWeight: 580,
              fontSize: 14,
              color: '#0D1B27'
            }}>
            {triggeredAt}
          </Typography>
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            {...getSelectorAttribute(headerSelectors.notifications.notificationCardContent.cardMetaData)}
            sx={{
              fontSize: 14,
              color: '#7e8796'
            }}>
            {metadata}
          </Typography>
          {notificationStatusIcon}
        </Box>

        {isFailed && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              variant="text"
              sx={{ fontSize: '12px' }}
              onClick={handleDismiss}
              disabled={isDismissing}
              {...getSelectorAttribute(headerSelectors.notifications.notificationCardContent.dismissButton)}
            >
              {isDismissing ? 'Dismissing...' : 'Dismiss'}
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIosIcon sx={{ fontSize: '10px !important' }} />}
              sx={{ border: '1px solid #A4ADBE', fontSize: '12px', height: '30px', borderRadius: '4px' }}
              onClick={handleOpen}
              {...getSelectorAttribute(
                headerSelectors.notifications.notificationCardContent.reviewNotificationButton,
              )}
            >
              Review
            </Button>
          </Box>
        )}
      </Box>
      <VyTasksModal
        open={isDialogOpen}
        onClose={handleClose}
        jobId={id}
        jobTitle={title}
        taskStatusFilter={taskStatusFilter}
      />
    </Box>
  );
};
